/**
 * AI Usage (2026-10-19): what the practice's OpenAI calls (Ask Velora on
 * gpt-5.4-mini, clinical notes on gpt-5.6-luna, all under a signed BAA) used and
 * what they are billed at. Calculate and display only: nothing here charges
 * a card, writes an invoice or triggers an automation.
 *
 * Money is integer micro-dollars (1 USD = 1,000,000 micros) everywhere.
 * Prices are micro-dollars per 1M tokens, so a price of 750,000 is
 * $0.75 / 1M tokens. The billing multiplier is in ten-thousandths
 * (`multiplierBp`: 20,000 = 2x).
 *
 *   cost   = (input - cached) x input price + cached x cached price
 *            + output x output price
 *   billed = cost x multiplier
 *
 * Prices are locked in per call: each recorded call stores its cost under
 * the pricing in effect then and the multiplier then in effect, and reports
 * sum the stored values. Changing prices or the multiplier only affects
 * calls recorded afterwards.
 *
 * Who sees what (Zac, 2026-10-19): everyone in the practice with
 * `marketplace.view`, the practice owner included, sees requests, tokens and
 * the billed amount only. The OpenAI cost, the multiplier and the per-model
 * prices are C2G's: only a Computers2Go support account (an
 * `identity.user_account.is_support` account, signed in as a practice user
 * through support sign-in, or holding the support-only `support.impersonate`
 * of the C2G Support role) sees them or edits pricing. The API strips them
 * server-side for everyone else.
 */
import { z } from 'zod';

/** Permission to open the AI Usage page (billed amounts only). */
export const AI_USAGE_VIEW_PERMISSION = 'marketplace.view';
/** The C2G Support role's support-only key; no practice role can hold it. */
export const AI_USAGE_C2G_PERMISSION = 'support.impersonate';

/** OpenAI standard-tier list prices on this date (code defaults). */
export const AI_PRICING_AS_OF = '2026-10-19';

export const ModelPriceSchema = z
  .object({
    model: z
      .string()
      .min(1)
      .max(100)
      .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/),
    /** Micro-dollars per 1M uncached input tokens. */
    inputMicros: z.number().int().min(0).max(1_000_000_000),
    /** Micro-dollars per 1M cached input tokens. */
    cachedInputMicros: z.number().int().min(0).max(1_000_000_000),
    /** Micro-dollars per 1M output tokens. */
    outputMicros: z.number().int().min(0).max(1_000_000_000),
  })
  .strict();
export type ModelPrice = z.infer<typeof ModelPriceSchema>;

/** Code defaults: OpenAI standard tier, USD per 1M tokens, as of `AI_PRICING_AS_OF`. */
export const DEFAULT_AI_PRICES: readonly ModelPrice[] = Object.freeze([
  // gpt-5.6-luna: $0.20 in, $0.02 cached in, $1.20 out (clinical notes primary).
  {
    model: 'gpt-5.6-luna',
    inputMicros: 200_000,
    cachedInputMicros: 20_000,
    outputMicros: 1_200_000,
  },
  // gpt-5.4-mini: $0.75 in, $0.075 cached in, $4.50 out.
  {
    model: 'gpt-5.4-mini',
    inputMicros: 750_000,
    cachedInputMicros: 75_000,
    outputMicros: 4_500_000,
  },
  // gpt-5.5: $5.00 in, $0.50 cached in, $30.00 out.
  {
    model: 'gpt-5.5',
    inputMicros: 5_000_000,
    cachedInputMicros: 500_000,
    outputMicros: 30_000_000,
  },
]);

/** Billed = cost x 2 unless C2G changes it. */
export const DEFAULT_MULTIPLIER_BP = 20_000;
export const MAX_MULTIPLIER_BP = 1_000_000; // 100x

/** What a call was for, as the page groups it. */
export const AI_USAGE_FEATURES = ['ask', 'notes', 'txplan', 'briefs', 'other'] as const;
export type AiUsageFeature = (typeof AI_USAGE_FEATURES)[number];
export const AI_USAGE_FEATURE_LABELS: Readonly<Record<AiUsageFeature, string>> = {
  ask: 'Ask Velora',
  notes: 'Clinical notes',
  txplan: 'Treatment plans',
  briefs: 'Patient briefs',
  other: 'Other AI',
};

/** Gateway purpose -> page feature. */
export function aiUsageFeatureOf(purpose: string): AiUsageFeature {
  if (purpose === 'ask.reason') return 'ask';
  if (purpose === 'scribe.note') return 'notes';
  if (purpose === 'txplan.narrative') return 'txplan';
  if (purpose === 'patient.brief') return 'briefs';
  return 'other';
}

export const AiUsageMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export const AiUsageReportQuerySchema = z
  .object({
    /** `YYYY-MM` in the practice's time zone; default the current month. */
    month: AiUsageMonthSchema.optional(),
  })
  .strict();
export type AiUsageReportQuery = z.infer<typeof AiUsageReportQuerySchema>;

export const AiUsageTotalsSchema = z
  .object({
    requests: z.number().int().min(0),
    /** All input tokens, cached included. */
    inputTokens: z.number().int().min(0),
    cachedInputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    /** Requests whose counts were estimated (the response carried no usage). */
    estimatedRequests: z.number().int().min(0),
    /** Requests on a model with no price (not in cost or billed). */
    unpricedRequests: z.number().int().min(0),
    /** Billed amount in micro-dollars; null when nothing in the bucket is priced. */
    billedMicros: z.number().int().min(0).nullable(),
    /** OpenAI cost in micro-dollars. C2G support only: absent for everyone else. */
    costMicros: z.number().int().min(0).nullable().optional(),
  })
  .strict();
export type AiUsageTotals = z.infer<typeof AiUsageTotalsSchema>;

export const AiUsageReportSchema = z
  .object({
    month: AiUsageMonthSchema,
    /** The practice's current month (newest the month picker offers). */
    currentMonth: AiUsageMonthSchema,
    timeZone: z.string(),
    /** True when the office selector is "All offices" (practice-wide calls included). */
    allOffices: z.boolean(),
    locationIds: z.array(z.uuid()),
    /** The caller is C2G support: sees OpenAI cost and edits pricing. */
    canSeeCost: z.boolean(),
    /**
     * Add-on billing is live (Marketplace `billingLive`). False or absent is
     * the beta no-billing hold: amounts are a usage value, not owed.
     */
    billingLive: z.boolean().optional(),
    totals: AiUsageTotalsSchema,
    byDay: z.array(AiUsageTotalsSchema.extend({ date: z.string() })),
    byFeature: z.array(
      AiUsageTotalsSchema.extend({ feature: z.enum(AI_USAGE_FEATURES), label: z.string() }),
    ),
    byOffice: z.array(
      AiUsageTotalsSchema.extend({ locationId: z.uuid().nullable(), name: z.string() }),
    ),
    /** Models seen this month with no price: flagged, not billed (C2G support only; empty otherwise). */
    unpricedModels: z.array(z.object({ model: z.string(), requests: z.number().int() })),
  })
  .strict();
export type AiUsageReport = z.infer<typeof AiUsageReportSchema>;

export const AiUsageSettingsSchema = z
  .object({
    prices: z.array(ModelPriceSchema),
    multiplierBp: z.number().int().min(0).max(MAX_MULTIPLIER_BP),
    /** The code defaults, for "reset" and to show what changed. */
    defaults: z.object({
      prices: z.array(ModelPriceSchema),
      multiplierBp: z.number().int(),
      asOf: z.string(),
    }),
    updatedAt: z.string().nullable(),
  })
  .strict();
export type AiUsageSettings = z.infer<typeof AiUsageSettingsSchema>;

export const AiUsageSettingsPatchSchema = z
  .object({
    prices: z
      .array(ModelPriceSchema)
      .max(50)
      .refine((l) => new Set(l.map((p) => p.model)).size === l.length, {
        message: 'Each model may be priced once.',
      })
      .optional(),
    multiplierBp: z.number().int().min(0).max(MAX_MULTIPLIER_BP).optional(),
  })
  .strict();
export type AiUsageSettingsPatch = z.infer<typeof AiUsageSettingsPatchSchema>;
